/*
 * ButtonBase.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { Key } from "ts-key-enum";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { OverridableComponent, OverrideComponentProps } from "../OverridableComponent";

import "./ButtonBase.scss";

export interface IButtonBaseProps {
	disabled?: boolean;
}

export const ButtonBase: OverridableComponent<IButtonBaseProps, "button"> = React.forwardRef(
	(
		{
			children,
			className,
			Component,
			onKeyDown,
			onClick,
			disabled,
			type,
			...props
		}: OverrideComponentProps<IButtonBaseProps, "button">,
		ref
	) => {
		const baseClass = BEM("edt-button-base", null, { disabled });

		if (className) {
			baseClass.add(className);
		}

		const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLButtonElement>>((event) => {
			if (event.target === event.currentTarget && event.key === " ") {
				event.preventDefault();
			}

			if (isFunction(onKeyDown)) {
				onKeyDown(event);
			}

			// Keyboard accessibility for non interactive elements
			if (event.target === event.currentTarget && event.key === Key.Enter && !disabled) {
				event.preventDefault();
				if (isFunction(onClick)) {
					onClick(event as unknown as React.MouseEvent<HTMLButtonElement, MouseEvent>); // Hack this it
				}
			}
		}, []);

		const buttonProps: Record<string, unknown> = {};

		if (Component === "button") {
			buttonProps.disabled = disabled;
			buttonProps.type = type;
		} else {
			if (Component !== "a") {
				buttonProps.role = "button";
			}
			buttonProps["aria-disabled"] = disabled;
		}

		return (
			<Component
				className={baseClass.toClassName()}
				ref={ref}
				onKeyDown={handleKeyDown}
				onClick={onClick}
				{...buttonProps}
				{...props}
			>
				{children}
			</Component>
		);
	}
);

ButtonBase.displayName = "ButtonBase";

ButtonBase.defaultProps = {
	Component: "button"
};
